/**
 * Runs the catalog update check once on load and exposes the result plus the
 * action that applies it.
 *
 * Deliberately a single check at mount rather than a poll. The catalog changes
 * at most weekly (the refresh workflow opens a PR that a human merges), so
 * polling would spend requests on a near-certain "no" — and the next page load
 * catches it anyway.
 */

import type { Catalog } from "@schema/catalog.ts";
import { useCallback, useEffect, useState } from "react";
import { checkForCatalogUpdate, refetchCatalog } from "./update.ts";

export type UpdateState = "idle" | "available" | "applying" | "failed";

export interface CatalogUpdate {
  state: UpdateState;
  apply: () => void;
  dismiss: () => void;
}

export function useCatalogUpdate(onUpdated: (catalog: Catalog) => void): CatalogUpdate {
  const [state, setState] = useState<UpdateState>("idle");

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    checkForCatalogUpdate(controller.signal)
      .then((result) => {
        if (live && result.stale) setState("available");
      })
      // A failed check is not an update. Offline is normal here.
      .catch(() => undefined);
    return () => {
      live = false;
      controller.abort();
    };
  }, []);

  const apply = useCallback(() => {
    setState("applying");
    refetchCatalog()
      .then((catalog) => {
        onUpdated(catalog);
        setState("idle");
      })
      .catch(() => setState("failed"));
  }, [onUpdated]);

  const dismiss = useCallback(() => setState("idle"), []);

  return { state, apply, dismiss };
}
